import { useSafeLayoutEffect } from '@hover/blueprint/chakra';
import { useScroll, useTransform } from 'framer-motion';
import type { RefObject } from 'react';
import { useState } from 'react';

import { desktopToolbarHeight } from './constants';

export interface ToolbarScrollOptions<
  TElement extends HTMLElement = HTMLElement,
> {
  multiplier?: number;
  offsetElementRef?: RefObject<TElement>;
}

/**
 * Interpolate a value based on scroll position until
 * the toolbar is fully in its "floating" styled state
 *
 * @param start value to interpolate from
 * @param end value to interpolate to
 * @param multiplier multiplier to apply to final scroll position to adjust the
 * interpolation relative to the toolbar interpolation
 *
 * @returns motion value for use with Framer Motion
 */

const useToolbarScrollValue = <TValue extends {}, TElement extends HTMLElement>(
  start: TValue,
  end: TValue,
  options: ToolbarScrollOptions<TElement> = {},
) => {
  const [offset, setOffset] = useState(0);

  const { multiplier = 1, offsetElementRef } = options;
  const { scrollY } = useScroll();

  useSafeLayoutEffect(() => {
    if (!offsetElementRef?.current) {
      setOffset(0);
      return;
    }

    const { top } = offsetElementRef.current.getBoundingClientRect();

    if (top > 0) setOffset(top);
  }, [offsetElementRef]);

  return useTransform(
    scrollY,
    [offset, desktopToolbarHeight * multiplier + offset],
    [start, end],
  );
};

export { useToolbarScrollValue };
